import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { setAuth } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Mail, RefreshCw, ArrowLeft } from 'lucide-react';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);

  const userId = searchParams.get('user_id');
  const email = searchParams.get('email') || '';
  const testOtp = searchParams.get('test_otp'); // dev/test convenience

  useEffect(() => {
    if (!userId) {
      toast.error('Verification session expired — please sign up again.');
      navigate('/signup');
      return;
    }
    // Prefill test OTP in dev
    if (testOtp && testOtp.length === 6) {
      setOtp(testOtp.split(''));
    }
    // Focus first input
    if (inputRefs.current[0]) inputRefs.current[0].focus();
  }, [userId, testOtp, navigate]);

  // Auto-submit when all 6 boxes are filled (covers URL-prefill + paste + typing)
  // Guarded by hasAutoSubmitted ref so we don't loop after a failed submit.
  const hasAutoSubmittedRef = useRef(false);
  useEffect(() => {
    if (loading) return;
    if (hasAutoSubmittedRef.current) return;
    if (otp.length === 6 && otp.every((d) => d && /^\d$/.test(d))) {
      hasAutoSubmittedRef.current = true;
      // Fire on next tick so the last box has time to render
      const t = setTimeout(() => handleSubmit(otp.join('')), 50);
      return () => clearTimeout(t);
    }
  }, [otp]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (resendCooldown > 0) {
      const t = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendCooldown]);

  const handleChange = (idx, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp];
    next[idx] = val.slice(0, 1);
    setOtp(next);
    if (val && idx < 5) inputRefs.current[idx + 1]?.focus();
    // Auto-submit when all 6 filled
    if (idx === 5 && val && next.every((d) => d)) {
      handleSubmit(next.join(''));
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData || window.clipboardData).getData('text').trim();
    if (/^\d{6}$/.test(pasted)) {
      e.preventDefault();
      setOtp(pasted.split(''));
      handleSubmit(pasted);
    }
  };

  const handleSubmit = async (code) => {
    const codeStr = code || otp.join('');
    if (codeStr.length !== 6) return;
    setLoading(true);
    try {
      const { data } = await api.post('/auth/verify-signup', {
        user_id: userId,
        otp_code: codeStr,
      });
      setAuth(data.access_token, data.user, data.accounts);
      // Carry paid-plan intent into the app so we can nudge the upgrade once.
      const plan = (searchParams.get('plan') || '').toLowerCase();
      if (plan && plan !== 'free') {
        localStorage.setItem('bp_upgrade_intent', plan);
      }
      toast.success('Welcome to BillPortal!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code. Try again.');
      setOtp(['', '', '', '', '', '']);
      hasAutoSubmittedRef.current = false;
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!userId || resendCooldown > 0) return;
    setLoading(true);
    try {
      const { data } = await api.post('/auth/resend-signup', { user_id: userId });
      setResendCooldown(60);
      toast.success('New code sent.');
      if (data?.test_otp) {
        setOtp(data.test_otp.split(''));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not resend code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="w-full max-w-md" data-testid="verify-email-page">
        <div className="inline-flex items-center gap-3 mb-8" data-testid="verify-brand">
          <img src="/billportal-logo.png" alt="BillPortal" className="h-9 w-9" />
          <span className="text-xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>BillPortal</span>
        </div>
        <button
          onClick={() => navigate('/signup')}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6 transition-colors"
          data-testid="verify-back-btn"
        >
          <ArrowLeft className="h-4 w-4"/>
          Back
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 bg-primary/10 flex items-center justify-center">
            <Mail className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>Verify your email</h1>
            <p className="text-sm text-slate-500">Enter the 6-digit code we sent</p>
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200 p-4 mb-6" data-testid="verify-email-target">
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Code sent to</p>
          <p className="font-mono text-sm font-medium">{email}</p>
        </div>

        <Label className="text-xs uppercase tracking-wide font-medium mb-3 block">
          Verification code
        </Label>
        <div className="flex gap-2 justify-between mb-6" onPaste={handlePaste}>
          {otp.map((digit, idx) => (
            <Input
              key={idx}
              ref={(el) => (inputRefs.current[idx] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(idx, e.target.value)}
              onKeyDown={(e) => handleKeyDown(idx, e)}
              className="w-12 h-14 text-center text-2xl font-bold rounded-none"
              data-testid={`verify-otp-input-${idx}`}
            />
          ))}
        </div>

        <Button
          onClick={() => handleSubmit()}
          disabled={loading || otp.some((d) => !d)}
          className="w-full rounded-none h-11 font-bold uppercase tracking-wide"
          data-testid="verify-submit-btn"
        >
          {loading ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin"/> Verifying…</>
          ) : (
            'Verify & continue'
          )}
        </Button>

        <div className="mt-6 text-center">
          <p className="text-xs text-slate-500 mb-2">Code expires in 10 minutes</p>
          <button
            onClick={handleResend}
            disabled={resendCooldown > 0 || loading}
            data-testid="verify-resend-btn"
            className={`inline-flex items-center gap-2 text-sm ${
              resendCooldown > 0 ? 'text-slate-400' : 'text-primary hover:underline'
            }`}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
          </button>
        </div>
      </div>
    </div>
  );
}
